// @ts-strict-ignore
import { Injectable } from "@angular/core";
import cloneDeep from "lodash-es/cloneDeep";
import { ExpertValidatorService } from "src/app/modules/esim/shared/services/expert-validator.service";
import { ExpertModeProfileDto } from "src/app/shared/models/expert-profile.model";
import { LogService } from "src/app/shared/services/log.service";
import { ExpertModeElement } from "../../components/expert/expert.component";
import { ProfileElement, ProfileElementSchema, ProfileElementScreenInput } from "../models/profile-element.model";
import { ExpertScreensGeneratorService } from "./expert-screens-generator.service";

@Injectable({ providedIn: "root" })
export class ExpertProfileService {
  constructor(
    private expertValidatorService: ExpertValidatorService,
    private logService: LogService,
    private expertScreensGeneratorService: ExpertScreensGeneratorService
  ) {}

  generateNewExpertModeProfile(activeSchema: ProfileElementSchema): ExpertModeProfileDto {
    let profileJson = this.generateBaseExpertModeProfile(activeSchema);

    const newExpertModeScreens = this.expertScreensGeneratorService.generateExpertModeScreens(profileJson, activeSchema);

    profileJson = this.generateSaveFile(activeSchema, profileJson, newExpertModeScreens);

    return profileJson;
  }

  generateBaseExpertModeProfile(activeSchema: ProfileElementSchema): ExpertModeProfileDto {
    // Perform a sanity check on key uuid uniqueness
    this.parseKeyUniqueness(activeSchema);

    const newProfile: ExpertModeProfileDto = {
      schemaKey: activeSchema.key,
      elements: [],
      exportData: null
    };

    for (const element of activeSchema.elements) {
      if (element.minInstances > 0) {
        for (let i = 0; i < element.minInstances; i++) {
          const newElement = this.generateBaseProfileElement(activeSchema, element.key);

          if (newElement) {
            newProfile.elements.push(newElement);
          }
        }
      }
    }

    return this.sortProfileElements(activeSchema, newProfile);
  }

  generateBaseProfileElement(activeSchema: ProfileElementSchema, key: string) {
    for (const element of activeSchema.elements) {
      if (element.key === key) {
        const newElement = cloneDeep({
          key: element.key,
          instanceId: crypto.randomUUID(),
          elementName: element.elementName,
          values: []
        });

        for (const screen of element.screens) {
          for (const input of screen.inputs) {
            newElement.values.push(
              cloneDeep({
                key: input.key,
                instanceId: crypto.randomUUID(),
                propertyName: input.propertyName,
                value: input.value ?? null
              })
            );
          }
        }

        return newElement;
      }
    }

    return null;
  }

  generateSaveFile(activeSchema: ProfileElementSchema, editedProfile: ExpertModeProfileDto, expertModeScreens: ExpertModeElement[]) {
    const newProfile: ExpertModeProfileDto = {
      ...editedProfile,
      schemaKey: activeSchema.key,
      elements: []
    };

    const newExportData = [];
    let directExport = null;

    for (const element of expertModeScreens) {
      const newValues = [];
      const newValuesForExportData = {};
      const newElement = {
        key: element.key,
        instanceId: element.instanceId,
        elementName: element.elementName,
        values: []
      };

      for (const screen of element.screens) {
        for (const input of screen.inputs) {
          directExport = this.generateSaveFileNested(activeSchema, expertModeScreens, input, newValues, newValuesForExportData);
        }
      }

      newProfile.elements.push({ ...newElement, values: [...newValues] });

      newExportData.push({});
      newExportData[newExportData.length - 1][element.elementName] = newValuesForExportData;
    }

    if (directExport) {
      newProfile.exportData = cloneDeep(directExport);
    } else {
      newProfile.exportData = cloneDeep(newExportData);
    }

    return newProfile;
  }

  generateSaveFileNested(
    activeSchema: ProfileElementSchema,
    expertModeScreens: ExpertModeElement[],
    input,
    newValues,
    newValuesForExportData
  ) {
    let directExport = null;

    if (input.type !== "displaytext") {
      let exportValue = false;

      const ignoreNullAtSchemaLevel = activeSchema.flattening?.ignoreNullValues ?? null;
      const ignoreNullAtInputLevel = input.flattening?.ignoreNullValues ?? null;

      if (input.type === "array" || input.type === "object") {
        const arrayValues = [];
        const arrayValuesForExportData = [];
        const objectValueForExportData = {};

        if (input.value) {
          for (const record of input.value) {
            const recordValues = [];
            const recordValuesForExportData = {};

            for (const arrayInput of record) {
              if (arrayInput.type !== "displaytext") {
                const ignoreNullAtPropertyLevel = arrayInput.flattening?.ignoreNullValues ?? null;

                if (arrayInput.flattening?.ignoreInput) {
                  exportValue = false;
                } else if (arrayInput.value && arrayInput.value !== null) {
                  exportValue = true;
                } else {
                  if (ignoreNullAtPropertyLevel !== null) {
                    exportValue = !ignoreNullAtPropertyLevel;
                  } else if (ignoreNullAtInputLevel !== null) {
                    exportValue = !ignoreNullAtInputLevel;
                  } else if (ignoreNullAtSchemaLevel !== null) {
                    exportValue = !ignoreNullAtSchemaLevel;
                  } else {
                    exportValue = true;
                  }
                }

                // If the value would be exported, check if it shouldn't be based on it being not enabled
                if (exportValue && arrayInput.flattening?.ignoreIfNotEnabled) {
                  if (!this.expertValidatorService.enableCheckInput(arrayInput, expertModeScreens)) {
                    exportValue = false;
                  }
                }

                if (arrayInput.type === "object" || arrayInput.type === "array") {
                  directExport = this.generateSaveFileNested(
                    activeSchema,
                    expertModeScreens,
                    arrayInput,
                    recordValues,
                    recordValuesForExportData
                  );

                  if (exportValue) {
                    if (arrayInput.type === "array") {
                      if (arrayInput.flattening?.arrayFlatteningStyle === "singlearray") {
                        // Seed the correct target depending on the parent input type
                        if (input.type === "array") {
                          arrayValuesForExportData.push(recordValuesForExportData);
                        } else if (input.type === "object") {
                          objectValueForExportData[arrayInput.propertyName] = recordValuesForExportData[arrayInput.propertyName];
                        }
                      } else if (arrayInput.flattening?.arrayFlatteningStyle === "nullproperties") {
                        objectValueForExportData[arrayInput.value] = null;
                      } else if (arrayInput.flattening?.arrayFlatteningStyle === "keyvaluepairs") {
                        // Nothing to do here (yet) apart from trapping the case from else
                      } else {
                        // Seed the correct target depending on the parent input type
                        if (input.type === "array") {
                          recordValuesForExportData[arrayInput.propertyName] = cloneDeep(
                            recordValuesForExportData[arrayInput.propertyName]
                          );
                        } else if (input.type === "object") {
                          objectValueForExportData[arrayInput.propertyName] = cloneDeep(recordValuesForExportData[arrayInput.propertyName]);
                        }
                      }
                    } else if (arrayInput.type === "object") {
                      objectValueForExportData[arrayInput.propertyName] = recordValuesForExportData[arrayInput.propertyName];
                    }
                  }
                } else {
                  recordValues.push({
                    key: arrayInput.key,
                    instanceId: arrayInput.instanceId,
                    propertyName: arrayInput.propertyName,
                    value: arrayInput.value
                  });

                  if (exportValue) {
                    if (input.type === "array") {
                      if (input.flattening?.arrayFlatteningStyle === "singlearray") {
                        arrayValuesForExportData.push(arrayInput.value);
                      } else if (input.flattening?.arrayFlatteningStyle === "nullproperties") {
                        objectValueForExportData[arrayInput.value] = null;
                      } else if (input.flattening?.arrayFlatteningStyle === "keyvaluepairs") {
                        // Nothing to do here (yet) apart from trapping the case from else
                      } else {
                        recordValuesForExportData[arrayInput.propertyName] = this.parseValue(arrayInput, arrayInput.value);
                      }
                    } else if (input.type === "object") {
                      objectValueForExportData[arrayInput.propertyName] = this.parseValue(arrayInput, arrayInput.value);
                    }
                  }
                }
              }
            }

            arrayValues.push(recordValues);

            if (input.type === "array") {
              if (input.flattening?.arrayFlatteningStyle !== "singlearray" && input.flattening?.arrayFlatteningStyle !== "nullproperties") {
                arrayValuesForExportData.push(recordValuesForExportData);
              }
            }
          }
        }

        newValues.push({
          key: input.key,
          instanceId: input.instanceId,
          propertyName: input.propertyName,
          value: arrayValues
        });

        if (input.flattening?.ignoreInput) {
          exportValue = false;
        } else if (
          (input.type === "array" &&
            input.flattening?.arrayFlatteningStyle !== "nullproperties" &&
            JSON.stringify(arrayValuesForExportData) !== "[]") ||
          (input.type === "array" &&
            input.flattening?.arrayFlatteningStyle === "nullproperties" &&
            JSON.stringify(objectValueForExportData) !== "{}") ||
          (input.type === "object" && JSON.stringify(objectValueForExportData) !== "{}")
        ) {
          exportValue = true;
        } else {
          if (ignoreNullAtInputLevel !== null) {
            exportValue = !ignoreNullAtInputLevel;
          } else if (ignoreNullAtSchemaLevel !== null) {
            exportValue = !ignoreNullAtSchemaLevel;
          } else {
            exportValue = true;
          }
        }

        // If the value would be exported, check if it shouldn't be based on it being not enabled
        if (exportValue && input.flattening?.ignoreIfNotEnabled) {
          if (!this.expertValidatorService.enableCheckInput(input, expertModeScreens)) {
            exportValue = false;
          }
        }

        if (exportValue) {
          if (input.type === "array") {
            if (input.flattening?.arrayFlatteningStyle === "nullproperties") {
              newValuesForExportData[input.propertyName] = objectValueForExportData;
            } else if (input.flattening?.arrayFlatteningStyle === "keyvaluepairs") {
              for (const record of input.value) {
                let key: string | null = null;
                let value: ProfileElementScreenInput["value"] = null;

                for (const recordInput of record) {
                  if (recordInput.propertyName === input.flattening.keyValuePairs?.keyPropertyName) {
                    key = recordInput.value;
                  } else if (recordInput.propertyName === input.flattening.keyValuePairs?.valuePropertyName) {
                    // If the value input is a complex data type, flatten its value in nested calculation first
                    if (recordInput.type === "object" || recordInput.type === "array") {
                      const newValuesComplex = [];
                      const newValuesComplexForExportData = [];

                      directExport = this.generateSaveFileNested(
                        activeSchema,
                        expertModeScreens,
                        recordInput,
                        newValuesComplex,
                        newValuesComplexForExportData
                      );

                      if (newValuesComplexForExportData) {
                        if (newValuesComplexForExportData[recordInput.propertyName]) {
                          value = newValuesComplexForExportData[recordInput.propertyName];
                        }
                      }
                    } else {
                      value = this.parseValue(recordInput, recordInput.value);
                    }
                  }
                }

                // If we found a valid key match (value can be null) in the record pairing to the configuration, push it into the housing object
                if (key) {
                  newValuesForExportData[key] = value;
                }
              }
            } else if (input.flattening?.arrayFlatteningStyle === "nestedarray") {
              // This operator is only designed to work with array inputs that have exactly one array child input
              if (input.inputs?.length === 1 && input.inputs[0].type === "array") {
                if (arrayValuesForExportData?.length > 0) {
                  newValuesForExportData[input.propertyName] = [arrayValuesForExportData[0][input.inputs[0].label]];
                } else {
                  newValuesForExportData[input.propertyName] = arrayValuesForExportData;
                }
              }
            } else {
              newValuesForExportData[input.propertyName] = arrayValuesForExportData;
            }
          } else if (input.type === "object") {
            newValuesForExportData[input.propertyName] = objectValueForExportData;
          }
        }
      } else {
        newValues.push({
          key: input.key,
          instanceId: input.instanceId,
          propertyName: input.propertyName,
          value: input.value
        });

        if (input.flattening?.ignoreInput) {
          exportValue = false;
        } else if (input.value && input.value !== null) {
          exportValue = true;
        } else {
          if (ignoreNullAtInputLevel !== null) {
            exportValue = !ignoreNullAtInputLevel;
          } else if (ignoreNullAtSchemaLevel !== null) {
            exportValue = !ignoreNullAtSchemaLevel;
          } else {
            exportValue = true;
          }
        }

        // If the value would be exported, check if it shouldn't be based on it being not enabled
        if (exportValue && input.flattening?.ignoreIfNotEnabled) {
          if (!this.expertValidatorService.enableCheckInput(input, expertModeScreens)) {
            exportValue = false;
          }
        }

        if (exportValue) {
          newValuesForExportData[input.propertyName] = this.parseValue(input, input.value);

          if (input.flattening?.directExport) {
            directExport = this.parseValue(input, input.value);
          }
        }
      }
    }

    return directExport;
  }

  parseValue(input: ProfileElementScreenInput, value: ProfileElementScreenInput["value"]) {
    if (input.type === "string" && input.flattening?.stringToJSON) {
      try {
        return JSON.parse(input.value);
      } catch (e) {
        return {}; // If we are unable to parse the JSON return an empty object
      }
    } else if (input.type === "code") {
      try {
        return JSON.parse(this.expertValidatorService.removeComments(input.value));
      } catch (e) {
        return {}; // If we are unable to parse the JSON return an empty object
      }
    } else {
      return value;
    }
  }

  getProfileElementDefinition(activeSchema: ProfileElementSchema, key: string): ProfileElement | null {
    for (const element of activeSchema.elements) {
      if (element.key === key) {
        return element;
      }
    }

    return null;
  }

  sortProfileElements(activeSchema: ProfileElementSchema, profile: ExpertModeProfileDto) {
    let rerunSort = false;

    const sortedProfile: ExpertModeProfileDto = {
      ...profile,
      elements: []
    };

    for (const element of profile.elements) {
      let isAlreadyAdded = false;

      for (const sortedElement of sortedProfile.elements) {
        if (element.instanceId === sortedElement.instanceId) {
          isAlreadyAdded = true;
          break;
        }
      }

      // If the current element has not already been pushed to the sorted stack then trigger required processing to add it
      if (!isAlreadyAdded) {
        const profileElementDefinition = this.getProfileElementDefinition(activeSchema, element.key);

        // Check if this profile element requires that it be after another one, if so attempt to find it and push that to the stack before adding the current element which requires it
        if (profileElementDefinition) {
          if (profileElementDefinition.locateAfter) {
            let isParentFound = false;

            for (const sortedElement of sortedProfile.elements) {
              if (sortedElement.key === profileElementDefinition.locateAfter) {
                isParentFound = true;
                break;
              }
            }

            if (!isParentFound) {
              for (const unsortedElement of profile.elements) {
                if (unsortedElement.key === profileElementDefinition.locateAfter) {
                  const parentProfileElementDefinition = this.getProfileElementDefinition(activeSchema, unsortedElement.key);

                  // If this element we are injecting itself requires that it be located after something, see if that is already present, if not trigger a resort
                  if (parentProfileElementDefinition.locateAfter) {
                    let isParentOfParentFound = false;

                    for (const sortedElement of sortedProfile.elements) {
                      if (sortedElement.key === parentProfileElementDefinition.locateAfter) {
                        isParentOfParentFound = true;
                        break;
                      }
                    }

                    if (!isParentOfParentFound) {
                      rerunSort = true;
                    }
                  }

                  sortedProfile.elements.push(unsortedElement);
                  break;
                }
              }
            }
          }
        }

        sortedProfile.elements.push(element);
      }
    }

    // Find any element denoted with "start" profilePosition and move it to the top
    for (let i = 0; i < sortedProfile.elements.length; i++) {
      const profileElementDefinition = this.getProfileElementDefinition(activeSchema, sortedProfile.elements[i].key);

      if (profileElementDefinition) {
        if (profileElementDefinition.profilePosition === "start") {
          const movedItem = { ...sortedProfile.elements[i] };

          sortedProfile.elements.splice(i, 1);
          sortedProfile.elements.unshift(movedItem);

          break;
        }
      }
    }

    // Find any element denoted with "end" profilePosition and move it to the end
    for (let i = 0; i < sortedProfile.elements.length; i++) {
      const profileElementDefinition = this.getProfileElementDefinition(activeSchema, sortedProfile.elements[i].key);

      if (profileElementDefinition) {
        if (profileElementDefinition.profilePosition === "end") {
          const movedItem = { ...sortedProfile.elements[i] };

          sortedProfile.elements.splice(i, 1);
          sortedProfile.elements.push(movedItem);

          break;
        }
      }
    }

    if (rerunSort) {
      return this.sortProfileElements(activeSchema, sortedProfile);
    } else {
      return sortedProfile;
    }
  }

  parseKeyUniqueness(activeSchema: ProfileElementSchema) {
    function parseKeyUniquenessNested(input: ProfileElementScreenInput, keys: string[]) {
      if (input.type !== "displaytext") {
        if (!keys.includes(input.key)) {
          keys.push(input.key);
        }

        if (input.type === "array" || input.type === "object") {
          if (input.inputs) {
            for (const nestedInput of input.inputs) {
              parseKeyUniquenessNested(nestedInput, keys);
            }
          }
        }
      }
    }

    const keys: string[] = [];

    for (const profileElement of activeSchema.elements) {
      if (!keys.includes(profileElement.key)) {
        keys.push(profileElement.key);
      }

      for (const screen of profileElement.screens) {
        if (!keys.includes(screen.key)) {
          keys.push(screen.key);
        }

        for (const input of screen.inputs) {
          parseKeyUniquenessNested(input, keys);
        }
      }
    }
  }
}
